<template>
    <div>
        <v-container grid-list-md>
            <BaseDatePicker
                :start.sync="dateRange.start"
                :end.sync="dateRange.end"
                :range.sync="range"
            >
                <v-progress-circular
                    slot="loader"
                    v-if="$apollo.queries.singleTrapStats.loading"
                    :size="30"
                    color="primary"
                    indeterminate
                ></v-progress-circular>
            </BaseDatePicker>
        </v-container>

        <i18n path="label.has_been_catched" tag="div" class="subheading">
            <v-chip slot="n" class="subheading font-weight-medium" dark color="primary">{{
                totalCatches
            }}</v-chip>
            <strong slot="start">{{
                dateRange.start | dateFormat({ month: "short" }, fullLocale)
            }}</strong>
            <strong slot="end">{{
                dateRange.end | dateFormat({ month: "short" }, fullLocale)
            }}</strong>
        </i18n>
        <v-btn @click="exportStats" v-t="'button.csv_export'" :loading="exportLoading"></v-btn>

        <div class="Chart" v-if="singleTrapStats">
            <h2 v-t="'title.chart_catch'"></h2>

            <ChartBar
                style="height: 30vh; min-height: 250px;"
                :chart-data="stats"
                :options="optionsCatch"
            />
        </div>
        <div class="Chart" v-if="singleTrapStats">
            <h2 v-t="'title.chart_battery'"></h2>
            <ChartLineBattery
                style="height: 350px;"
                :chart-data="statsBattery"
                :options="optionsAvg"
            />
        </div>
        <div class="Chart" v-if="singleTrapStats">
            <h2 v-t="'title.chart_signal'"></h2>
            <ChartLineBattery
                style="height: 350px;"
                :chart-data="statsSignal"
                :options="optionsAvg"
            />
        </div>
        <div class="Chart" v-if="singleTrapStats">
            <h2 v-t="'title.chart_temperatures'"></h2>
            <ChartLineTemperature
                style="height: 350px;"
                :chart-data="statsTemperature"
                :options="optionsAvgCelcius"
            />
        </div>

        <v-layout v-else justify-center align-center class="headline">{{
            $t("message.no_results")
        }}</v-layout>
    </div>
</template>

<script>
import ChartBar from "@/components/charts/Bar"
import ChartLine from "@/components/charts/Line"
import ChartLineBattery from "@/components/charts/LineBattery"
import ChartLineTemperature from "@/components/charts/LineTemperature"
import stats from "@/mixins/stats.js"
import download from "@/utils/download.js"
import { mapGetters } from "vuex"
export default {
    mixins: [stats],
    components: {
        ChartBar,
        ChartLine,
        ChartLineTemperature,
        ChartLineBattery,
    },
    data() {
        return {
            dateRange: { start: null, end: null },
            range: "week",
            exportLoading: false,
        }
    },
    mounted() {
        this.$moment.locale(this.$i18n.locale)
        const day = 1000 * 60 * 60 * 24
        const now = new Date()
        now.removeTime()
        const baseWeeks = 20
        if (this.range == "week") {
            this.dateRange.end = new Date(now.getTime() + day * (7 - now.getDay()))

            this.dateRange.start = new Date(this.dateRange.end.getTime() - day * 7 * baseWeeks)
        }
    },
    props: {
        trapId: { type: String, required: true },
        trapName: { type: String, required: true },
    },
    methods: {
        async exportStats() {
            this.exportLoading = true
            try {
                const {
                    data: { exportSingleTrapStats },
                } = await this.$apollo.query({
                    fetchPolicy: "no-cache",
                    query: require("@/graphql/statistics/exportSingleTrapStats.gql"),
                    variables: {
                        trapId: this.trapId,
                        endDate: this.dateRange.end.toISOString(),
                        startDate: this.dateRange.start.toISOString(),
                        range: this.range,
                        locale: navigator.language,
                    },
                })
                download(exportSingleTrapStats, `${this.trapName}.csv`)
            } catch (e) {}
            this.exportLoading = false
        },
    },
    computed: {
        ...mapGetters({ fullLocale: "user/fullLocale" }),
        labels() {
            if (!this.singleTrapStats) {
                return null
            }
            return this.singleTrapStats.labels.map(l => new Date(Number(l)).removeTime())
        },
        totalCatches() {
            if (!this.singleTrapStats) {
                return null
            }
            return this.singleTrapStats.catches.reduce((a, v) => a + v, 0)
        },
        totalPings() {
            if (!this.singleTrapStats) {
                return null
            }
            return this.singleTrapStats.results.reduce((a, v) => a + v.catches, 0)
        },
        totalRearms() {
            if (!this.singleTrapStats) {
                return null
            }
            return this.singleTrapStats.results.reduce((a, v) => a + v.catches, 0)
        },

        stats() {
            if (!this.singleTrapStats) {
                return null
            }

            const data = []
            for (const key in this.labels) {
                data.push({
                    x: this.labels[key],
                    y: this.singleTrapStats.catches[key],
                })
            }
            return {
                datasets: [
                    {
                        backgroundColor: "rgba(0,139,108,0.4)",
                        borderColor: "rgba(0,139,108,1)",
                        data,
                    },
                ],
            }
        },
        statsBattery() {
            if (!this.singleTrapStats) {
                return null
            }

            const data = []
            for (const key in this.labels) {
                data.push({
                    x: this.labels[key],
                    y: Math.round(this.singleTrapStats.batteries[key]),
                })
            }
            return {
                datasets: [
                    {
                        data,
                    },
                ],
            }
        },
        statsSignal() {
            if (!this.singleTrapStats) {
                return null
            }

            const data = []
            for (const key in this.labels) {
                data.push({
                    x: this.labels[key],
                    y: Math.round(this.singleTrapStats.signals[key]),
                })
            }
            return {
                datasets: [
                    {
                        data,
                    },
                ],
            }
        },
        statsTemperature() {
            if (!this.singleTrapStats) {
                return null
            }

            const data = []
            for (const key in this.labels) {
                data.push({
                    x: this.labels[key],
                    y: Math.round(this.singleTrapStats.temperatures[key] * 100) / 100,
                })
            }
            return {
                datasets: [
                    {
                        data,
                    },
                ],
            }
        },
    },

    apollo: {
        singleTrapStats: {
            query: require("@/graphql/statistics/singleTrapStats.gql"),
            variables() {
                return {
                    trapId: this.trapId,
                    endDate: this.dateRange.end.toISOString(),
                    startDate: this.dateRange.start.toISOString(),
                    range: this.range,
                }
            },
            update({ singleTrapStats }) {
                this.unit = this.calculateUnit()
                return singleTrapStats
            },
            skip() {
                return !this.dateRange.end || !this.dateRange.start
            },
        },
    },
}
</script>

<style>
.Chart h2 {
    margin-top: 20px;
    margin-bottom: 10px;
    text-align: center;
}
</style>
